import React from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, Outlet, Navigate, useParams } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import theme from "./theme";
import { HTMLContentViewer } from "./components/ContentViewer";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import { AuthProvider } from "./AuthContext"; // Import AuthProvider
import Login from "Homepage/Login";
import PrivateRoutes from "./PrivateRoute";
import { Provider } from "react-redux";
import { store } from "./store";
import AboutUs from "Homepage/AboutUs/AboutUs";
import Dashboard from "dashboard";
import Workspace from "workspace";
import Profile from "../src/Profile/App.js";
import CalendarMeeting from "new-homepage/CalendarMeeting";
import PageNotFound from "PageNotFound";
import HomepageNew from "homepage-new/Homepage";
import LegacyPrepRedirect from "components/Flowstate/LegacyPrepRedirect";
import Reinforce from "reinforce/Reinforce";
import ModeratorLogin from "components/ModeratorLogin";
import AITutorHome from "components/AITutor/AITutorHome";
import AITutorLearning from "components/AITutor/AITutorLearning";
import BatchWorkspace from "workspace/BatchWorkspace";
import AssignmentPage from "workspace/AssignmentPage";
import SessionPage from "workspace/Sessions/SessionPage";
import ContentBankPage from "workspace/ContentBank/ContentBankPage";
import SubmissionsPage from "workspace/Submissions/SubmissionsPage";
import StudyLibrary from "workspace/Study/StudyLibrary";
import StudyReader from "workspace/Study/StudyReader";
import { AssessmentWorkspace, SubjectiveWorkspace, MCQWorkspace, QuestionTypePicker, MentorSubmissionsReview } from "components/Assessment";
import { QuestionLibrary } from "components/QuestionLibrary";
import { AssignmentLibrary } from "components/AssignmentLibrary";
import { TrackSelection, CategorySelection, TopicSelection, Workspace as FlowstateWorkspace, FlowstateThemeProvider, FlowstateAuthProvider, FlowstateProtectedRoute, FlowstateLogin, MentorshipInquiry, LegalPage, Feedback, PricingProvider, FlowstateDynamicRouter } from "components/Flowstate";

// Create React Query client for Flowstate
// Old per-batch Content Library links open the bank on that batch.
const BatchContentBankRedirect = () => {
  const { batchId } = useParams();
  return <Navigate to={`/dashboard/content-library?type=cohort&batch=${batchId}`} replace />;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

// Create emotion cache with prepend to ensure MUI styles come first
const cache = createCache({
  key: 'mui',
  prepend: true,
});


const container = document.getElementById("root");

// scripts/prerender.js bakes the homepage's markup into build/index.html. When
// that markup is present we hydrate it so the static paint is kept; otherwise
// this is an ordinary client render. A hydration mismatch is not fatal — React
// falls back to rendering on the client.
const prerendered = container.hasChildNodes();

const tree = (
  <Provider store={store}>
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
        <AuthProvider>
          <Routes>
          {/* <Route path="/" element={<NewHomepage />} /> */}
          <Route path="/" element={<Navigate to="/flowstate" replace />} />
          <Route path="/flowstate" element={<HomepageNew />} />
          <Route path="/flowstate/*" element={<LegacyPrepRedirect />} />
          <Route
            path="/reinforce"
            element={
              <QueryClientProvider client={queryClient}>
                <Reinforce />
              </QueryClientProvider>
            }
          />
          <Route path="/page-not-found" element={<PageNotFound />} />
          <Route path="/login" element={<Login />} />
          <Route path="/m0d-7x9k2" element={<ModeratorLogin />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route element={<PrivateRoutes />}>
            <Route path="/dashboard" element={<Dashboard />} />
            {/* The publisher now lives in the content bank */}
            <Route path="/dashboard/reading-material-publisher" element={<Navigate to="/dashboard/content-library" replace />} />
            <Route path="/dashboard/question-library" element={<QuestionLibrary />} />
            <Route path="/dashboard/assignment-library" element={<AssignmentLibrary />} />
            {/* Study Anything: your own uploaded books, with the AI tutor */}
            <Route path="/dashboard/study" element={<StudyLibrary />} />
            <Route path="/dashboard/study/:bookId" element={<StudyReader />} />
          </Route>
          <Route element={<PrivateRoutes />}>
            <Route path="/dashboard/workspace/:id" element={<Workspace />} />
            <Route path="/dashboard/workspace/:id/reading-material" element={<HTMLContentViewer />} />
            <Route path="/dashboard/workspace/:id/ai-tutor/:topicId" element={<AITutorHome />} />
            <Route path="/dashboard/workspace/:id/ai-tutor/learn/:sessionId" element={<AITutorLearning />} />
            <Route path="/dashboard/workspace/:id/assessment/:topicId" element={<QuestionTypePicker />} />
            <Route path="/dashboard/workspace/:id/assessment/:topicId/p/:problemId" element={<AssessmentWorkspace />} />
            <Route path="/dashboard/workspace/:id/assessment/:topicId/s/:problemId" element={<SubjectiveWorkspace />} />
            <Route path="/dashboard/workspace/:id/assessment/:topicId/o/:problemId" element={<MCQWorkspace />} />
            <Route path="/dashboard/workspace/:id/assessment/:topicId/review/:menteeId" element={<MentorSubmissionsReview />} />
            <Route path="/dashboard/workspace/:id/assignments/:topicId" element={<AssignmentPage />} />
            <Route path="/dashboard/batch/:batchId" element={<BatchWorkspace />} />
            <Route path="/dashboard/batch/:batchId/workspace" element={<BatchWorkspace />} />
            <Route path="/dashboard/batch/:batchId/sessions/:sessionId" element={<SessionPage />} />
            <Route path="/dashboard/workspace/:id/sessions/:sessionId" element={<SessionPage />} />
            <Route path="/dashboard/batch/:batchId/content-library/:topicTemplateId" element={<BatchContentBankRedirect />} />
            <Route path="/dashboard/batch/:batchId/reading-material" element={<HTMLContentViewer />} />
            <Route path="/dashboard/content-library" element={<ContentBankPage />} />
            <Route path="/dashboard/submissions" element={<SubmissionsPage />} />
          </Route>
          <Route element={<PrivateRoutes />}>
            <Route path="/calendar-meeting" element={<CalendarMeeting />} />
          </Route>
          <Route element={<PrivateRoutes />}>
            <Route path="/profile" element={<Profile />} />
          </Route>
          {/* Interview Prep */}
          <Route path="/interview-prep" element={
            <QueryClientProvider client={queryClient}>
              <PricingProvider>
                <FlowstateAuthProvider>
                  <FlowstateThemeProvider>
                    <Outlet />
                  </FlowstateThemeProvider>
                </FlowstateAuthProvider>
              </PricingProvider>
            </QueryClientProvider>
          }>
            <Route index element={<TrackSelection />} />
            <Route path="login" element={<FlowstateLogin />} />
            <Route path="mentorship-inquiry" element={<MentorshipInquiry />} />
            <Route path="terms" element={<LegalPage />} />
            <Route path="privacy" element={<LegalPage />} />
            <Route path="refund" element={<LegalPage />} />
            <Route path="feedback" element={<Feedback />} />
            <Route path="*" element={<FlowstateDynamicRouter />} />
          </Route>
        </Routes>
        </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </CacheProvider>
  </Provider>
);

// Remove FOUC protection after render
const showApp = () => {
  requestAnimationFrame(() => {
    container.classList.add('app-ready');
  });
};

if (prerendered) {
  hydrateRoot(container, tree);
  showApp();
} else {
  createRoot(container).render(tree);
  showApp();
}
